const LEGACY_STATIC_ARTICLE_LENGTH = 8
const ARTICLE_AUTHOR_FIELDS = ['id', 'name']

function isObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function validateLegacyStaticArticle(payload) {
  const valid =
    payload.length === LEGACY_STATIC_ARTICLE_LENGTH &&
    (typeof payload[0] === 'string' || typeof payload[0] === 'number') &&
    (payload[1] === null || (Array.isArray(payload[1]) && payload[1].length === 6)) &&
    typeof payload[2] === 'boolean' &&
    typeof payload[3] === 'boolean' &&
    (payload[4] === null || typeof payload[4] === 'string') &&
    Array.isArray(payload[5]) &&
    payload[5].every(author => Array.isArray(author) && author.length === ARTICLE_AUTHOR_FIELDS.length) &&
    Array.isArray(payload[6]) &&
    payload[6].every(lemma => Array.isArray(lemma) && lemma.length === 12) &&
    (payload[7] === null || isObject(payload[7]))

  if (!valid) {
    throw new Error('Static article data does not match the supported 8-field layout')
  }
}

function unwrapArticlePayload(payload) {
  if (Array.isArray(payload)) {
    validateLegacyStaticArticle(payload)
    return payload
  }
  if (!isObject(payload)) {
    throw new Error('Article data must be an object or positional array')
  }
  if ('schemaVersion' in payload) {
    if (payload.schemaVersion !== 1 || !isObject(payload.article)) {
      throw new Error(`Unsupported article schema version: ${payload.schemaVersion}`)
    }
    return payload.article
  }
  return payload
}

function normalizeArticleAuthor(author, index) {
  if (!isObject(author)) {
    throw new Error(`Article author ${index} must be an object`)
  }
  if (author.id === null || author.id === undefined || typeof author.name !== 'string') {
    throw new Error(`Article author ${index} is missing id or name`)
  }
  return {
    ...author,
    id: String(author.id),
  }
}

function normalizeArticleLemma(lemma, fallbackId) {
  if (!isObject(lemma)) {
    throw new Error('Article lemma must be an object')
  }
  const id = lemma.id === null || lemma.id === undefined ? fallbackId : lemma.id
  if (id === null || id === undefined || typeof lemma.lemmaForm !== 'string') {
    throw new Error('Article lemma is missing id or lemmaForm')
  }
  const tree = isObject(lemma.tree)
    ? {
      ...lemma.tree,
      manualNumber: lemma.tree.manualNumber ?? null,
    }
    : null
  if (!tree) {
    throw new Error(`Article lemma ${id} is missing tree metadata`)
  }
  return {
    ...lemma,
    id: String(id),
    partofspeeches: Array.isArray(lemma.partofspeeches) ? lemma.partofspeeches : [],
    scriptas: Array.isArray(lemma.scriptas) ? lemma.scriptas : [],
    tree,
  }
}

function normalizeLemmaTree(node, lemmaIds) {
  if (!isObject(node) || !Array.isArray(node.children)) {
    throw new Error('Article lemmaTree must contain an object with a children array')
  }
  const id = node.id === null || node.id === undefined ? null : String(node.id)
  if (id !== null && !lemmaIds.has(id)) {
    throw new Error(`Article lemmaTree references missing lemma ${id}`)
  }
  return {
    id,
    children: node.children.map(child => normalizeLemmaTree(child, lemmaIds)),
  }
}

function normalizeArticle(article) {
  if (!isObject(article)) {
    throw new Error('Article data is missing or malformed')
  }
  if (article.id === null || article.id === undefined) {
    throw new Error('Article data is missing its id')
  }
  if (!isObject(article.dictionary) || typeof article.dictionary.acronym !== 'string') {
    throw new Error('Article data is missing its dictionary')
  }

  const articleauthors = article.articleauthors === null || article.articleauthors === undefined
    ? []
    : article.articleauthors
  if (!Array.isArray(articleauthors)) {
    throw new Error('Article articleauthors must be an array')
  }

  const rawLemmas = article.lemmas
  if (!Array.isArray(rawLemmas) && !isObject(rawLemmas)) {
    throw new Error('Article lemmas must be an array or object')
  }
  const lemmaEntries = Array.isArray(rawLemmas)
    ? rawLemmas.map(lemma => [lemma.id, lemma])
    : Object.entries(rawLemmas)
  const lemmas = {}
  lemmaEntries.forEach(([fallbackId, lemma]) => {
    const normalized = normalizeArticleLemma(lemma, fallbackId)
    lemmas[normalized.id] = normalized
  })
  const lemmaIds = new Set(Object.keys(lemmas))
  const lemmaTree = article.lemmaTree
    ? normalizeLemmaTree(article.lemmaTree, lemmaIds)
    : { id: null, children: [] }
  const mainLemma = lemmaTree.id === null ? null : lemmas[lemmaTree.id]

  const dictionaryId = article.dictionaryId ??
    article.dictionary.id ??
    mainLemma?.articleDictionaryId ??
    null
  const dictionary = {
    ...article.dictionary,
    id: dictionaryId === null ? null : String(dictionaryId),
  }
  const rawArticleEtymology = article.articleEtymology
  if (rawArticleEtymology !== null &&
      rawArticleEtymology !== undefined &&
      !isObject(rawArticleEtymology)) {
    throw new Error('Article articleEtymology must be an object or null')
  }
  const articleEtymology = rawArticleEtymology === null || rawArticleEtymology === undefined
    ? { id: null, description: '', isDeafplus: false }
    : {
      ...rawArticleEtymology,
      description: rawArticleEtymology.description || '',
    }

  return {
    ...article,
    id: String(article.id),
    dictionaryId: dictionary.id,
    dictionary,
    articleauthors: articleauthors.map(normalizeArticleAuthor),
    articleEtymology,
    articleEtymon: isObject(article.articleEtymon) ? article.articleEtymon : null,
    lemmas,
    lemmaTree,
    mainLemma,
    mainLemmaId: mainLemma ? mainLemma.id : null,
    title: article.title || mainLemma?.articleMainLemmaForm || mainLemma?.lemmaForm || '',
  }
}

export {
  normalizeArticle,
  unwrapArticlePayload,
}
